import { FC, useState } from "react";
import { BaseModalWindow } from "../../../../components/modalWindows/BaseModalWindow";
import styles from '../../../../components/modalWindows/styles/BaseModalWindow.module.scss'
import stylesRadio from '../../../../components/modalWindows/styles/RadioControlModal.module.scss'
import stylesModal from './ActRevaluationCreatorItem/styles/ActRevaluationRecalcModal.module.scss'
import { useTranslation } from "react-i18next";
import { DecimalInputV2 } from "../../../../components/controls/inputs/BaseInput";
import { IOption, Select } from "../../../../components/selects/select";
import FlexRow from "../../../../components/controls/FlexRow";
import FlexColumn from "../../../../components/controls/FlexColumn";

interface IRadioOptionsModal {
    header: string;
    label: string;
    options: {
        value: string;
        label: string;
    }[];
    selectedValue?: string; // Для передачи начального значения, если нужно
    markupPercent?: number;
    onSelect: (selectedValue: string, markupPercent: number, markupOrMarkdownState: IMarkupOrMarkdownState) => void;
    cancel: () => void;
    close: () => void;
    hideOnOk?: boolean
}

export enum PriceAdjustmentType {
    MARKUP = 'markup',
    MARKDOWN = 'markdown'
}

export enum PriceAdjustmentMethod {
    ABSOLUTE = 'absolute',
    PERCENTAGE = 'percentage'
}

export enum AdjustmentFactorType {
    PERCENT = 'percent',
    TIMES = 'times'
}

export interface IMarkupOrMarkdownState {
    priceAdjustmentType: PriceAdjustmentType;
    priceAdjustmentMethod: PriceAdjustmentMethod;
    adjustmentFactorType: AdjustmentFactorType;
    percent: number;
    absolute: number;
}

export const ActRevaluationRecalcModal: FC<IRadioOptionsModal> = ({
    header,
    label,
    options,
    selectedValue: initialSelectedValue,
    onSelect,
    cancel,
    close,
    hideOnOk
}) => {
    const [selectedValue, setSelectedValue] = useState<string | undefined>(initialSelectedValue);
    const [markupPercent, setMarkupPercent] = useState<number>(0);
    const [markupOrMarkdownState, setMarkupOrMarkdownState] = useState<IMarkupOrMarkdownState>({
        priceAdjustmentType: PriceAdjustmentType.MARKUP,
        priceAdjustmentMethod: PriceAdjustmentMethod.PERCENTAGE,
        adjustmentFactorType: AdjustmentFactorType.PERCENT,
        percent: 0,
        absolute: 0
    });

    const { t } = useTranslation();
    const baseT = (value: string) => t("documents.actRevaluation." + value);

    const priceAdjustmentTypeOption = [
        { displayName:  baseT('markup'), value: PriceAdjustmentType.MARKUP },
        { displayName: baseT('markdown'), value: PriceAdjustmentType.MARKDOWN },
    ] as IOption[];

    const priceAdjustmentMethodOption = [
        { displayName: baseT('byChangePercent'), value: PriceAdjustmentMethod.PERCENTAGE },
        { displayName: baseT('byChangePrice'), value: PriceAdjustmentMethod.ABSOLUTE },
    ] as IOption[];

    const adjustmentFactorTypeOption = [
        { value: AdjustmentFactorType.PERCENT, label: baseT('in')},
        { value: AdjustmentFactorType.TIMES, label: baseT('to') },
    ];

    const handleOk = () => {
        if (selectedValue !== undefined) {
            onSelect(selectedValue, markupPercent, markupOrMarkdownState);
        }
        hideOnOk && close();
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSelectedValue(value);
    };

    const handlePercentChange = (value: number) => {
        setMarkupPercent(value);
    };

    const handlePriceAdjustmentTypeSelect = (value: IOption) => {
        setMarkupOrMarkdownState({...markupOrMarkdownState, priceAdjustmentType: value.value as PriceAdjustmentType})
    };

    const handlePriceAdjustmentMethodSelect = (value: IOption) => {
        setMarkupOrMarkdownState({...markupOrMarkdownState, priceAdjustmentMethod: value.value as PriceAdjustmentMethod})
    };

    const handleAdjustmentFactorTypeChange = (e, type: AdjustmentFactorType) => {
        e.stopPropagation()
        setMarkupOrMarkdownState({
            ...markupOrMarkdownState,
            adjustmentFactorType: type,
            absolute: 0
        })
    }

    return (
        <BaseModalWindow
            header={header}
            ok={{
                onClick: handleOk,
                title: t("general.ok")
            }}
            cancel={{
                onClick: () => { cancel(); close(); },
                title: t("general.cancel")
            }}
            modalWindowClassName={styles.modalWindowMovement}
        >
            <label>
                { label }
            </label>
            <div className={stylesRadio.radioOptionsContainer}>
                {options.map((option, index) => (
                    <label key={option.value || index} className={option.value === 'recountWithMarkupOrMarkdown' ? stylesRadio.radioOptionLabelWithBorder : stylesRadio.radioOptionLabel}>
                        <input
                            type="radio"
                            name="radioOptions"
                            className={stylesRadio.radioInput}
                            value={option.value}
                            checked={selectedValue === option.value}
                            onChange={handleChange}
                        />
                        <span className={stylesRadio.radioOptionText}>{option.label}</span>
                        {option.value === 'recountWithMarkupPercent' ? (
                            <div className={stylesModal.decimalInputWrapper}>
                                <DecimalInputV2
                                    value={markupPercent}
                                    onChange={handlePercentChange}
                                    onFocus={() => setSelectedValue(option.value)}
                                />
                            </div>
                        ) : null}
                        {option.value === 'recountWithMarkupOrMarkdown' ? (
                            <FlexColumn>
                                <FlexRow>
                                    <Select
                                        className={stylesModal.selectWrapper}
                                        options={priceAdjustmentTypeOption}
                                        defaultOption={priceAdjustmentTypeOption[0]}
                                        onSelect={(value) => {
                                            setSelectedValue(option.value);
                                            handlePriceAdjustmentTypeSelect(value);
                                        }}
                                    />
                                    <Select
                                        className={stylesModal.selectWrapper}
                                        options={priceAdjustmentMethodOption}
                                        defaultOption={priceAdjustmentMethodOption[0]}
                                        onSelect={(value) => {
                                            setSelectedValue(option.value);
                                            handlePriceAdjustmentMethodSelect(value);
                                        }}
                                    />
                                </FlexRow>
                                <div className={stylesModal.radioBlocWrapper}>
                                    <input
                                        type="radio"
                                        name="adjustmentFactorType"
                                        className={stylesRadio.radioInput}
                                        value={adjustmentFactorTypeOption[0].value}
                                        checked={markupOrMarkdownState.adjustmentFactorType === adjustmentFactorTypeOption[0].value}
                                        onChange={(e) => handleAdjustmentFactorTypeChange(e, AdjustmentFactorType.PERCENT)}
                                    />

                                    <div>{baseT('in')}</div>

                                    <DecimalInputV2
                                        className={stylesModal.decInputWrapper}
                                        value={markupOrMarkdownState.percent}
                                        onChange={(value: number) => setMarkupOrMarkdownState({ ...markupOrMarkdownState, percent: value })}
                                        disabled={markupOrMarkdownState.adjustmentFactorType === AdjustmentFactorType.TIMES}
                                        onFocus={() => setSelectedValue(option.value)}
                                    />

                                    <div className={stylesModal.percentWrapper}>%</div>

                                    <input
                                        type="radio"
                                        name="adjustmentFactorType"
                                        className={stylesRadio.radioInput}
                                        value={adjustmentFactorTypeOption[1].value}
                                        checked={markupOrMarkdownState.adjustmentFactorType === adjustmentFactorTypeOption[1].value}
                                        onChange={(e) => handleAdjustmentFactorTypeChange(e, AdjustmentFactorType.TIMES)}
                                    />

                                    <div>{baseT('to')}</div>

                                    <DecimalInputV2
                                        className={stylesModal.decInputWrapper}
                                        value={markupOrMarkdownState.absolute}
                                        onChange={(value: number) => setMarkupOrMarkdownState({ ...markupOrMarkdownState, absolute: value })}
                                        disabled={markupOrMarkdownState.adjustmentFactorType === AdjustmentFactorType.PERCENT}
                                        onFocus={() => setSelectedValue(option.value)}
                                    />
                                    <div>раз</div>
                                </div>
                            </FlexColumn>
                        ) : null}
                    </label>
                ))}
            </div>
        </BaseModalWindow>
    );
};
